/**
 * The join code: eight characters that are the whole session identity.
 *
 * The alphabet is Crockford base32 with the glyphs a person reads aloud wrong taken out: no `0` or
 * `O`, no `1`, `I` or `L`. That leaves thirty-one symbols and about forty bits over eight
 * characters. Codes are shown in two groups of four, `RUST-7F2K`, because that is what survives
 * being read down a phone line.
 *
 * A code is never repaired. With `0` and `1` out of the alphabet there is no honest character to
 * map a stray `O` onto, and a silent substitution produces a code that fails much later with a
 * confusing message, so parsing names the offending glyph and refuses.
 *
 * Nothing here touches the network. What the code becomes on the wire is
 * [roomIdentity](./roomIdentity.ts): the room id is a hash of it and the code itself is the
 * encryption password, so no relay operator ever sees this string.
 */

/** Thirty-one symbols, so a byte drawn at random needs rejection sampling, not a modulo. */
export const JOIN_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';

export const JOIN_CODE_LENGTH = 8;

/** Characters per readable group. Eight over four is the `RUST-7F2K` shape. */
export const JOIN_CODE_GROUP = 4;

/** Injected so a test can assert an exact code from an exact byte string. */
export type RandomBytes = (count: number) => Uint8Array;

/** Bytes at or above this would bias the low end of the alphabet, so they are thrown away. */
const REJECTION_LIMIT = 256 - (256 % JOIN_CODE_ALPHABET.length);

/** A draw of eight bytes yields six or seven characters on average, so a handful of rounds is
 *  plenty. The cap exists only so a broken byte source fails loudly instead of spinning. */
const MAX_DRAWS = 32;

const SEPARATORS = /[\s\-._]/g;

export type JoinCodeParse =
  | { readonly ok: true; readonly canonical: string }
  | {
      readonly ok: false;
      readonly reason: 'empty' | 'length' | 'character';
      readonly detail: string;
    };

/** Uniform over the alphabet, by rejection sampling. Returns the ungrouped canonical form. */
export function generateJoinCode(random: RandomBytes = cryptoBytes): string {
  let code = '';

  for (let draw = 0; draw < MAX_DRAWS && code.length < JOIN_CODE_LENGTH; draw += 1) {
    for (const byte of random(JOIN_CODE_LENGTH)) {
      if (byte >= REJECTION_LIMIT) continue;
      code += JOIN_CODE_ALPHABET.charAt(byte % JOIN_CODE_ALPHABET.length);
      if (code.length === JOIN_CODE_LENGTH) break;
    }
  }

  if (code.length < JOIN_CODE_LENGTH) {
    throw new Error('the random source did not yield a join code');
  }
  return code;
}

/** Canonical form to what a player reads: `RUST7F2K` becomes `RUST-7F2K`. */
export function formatJoinCode(canonical: string): string {
  const groups: string[] = [];
  for (let at = 0; at < canonical.length; at += JOIN_CODE_GROUP) {
    groups.push(canonical.slice(at, at + JOIN_CODE_GROUP));
  }
  return groups.join('-');
}

/**
 * What a player pasted to the canonical form, or a reason it is not a code.
 *
 * Case and separators are the only things forgiven, which covers a lowercase retype, a code with
 * spaces in it and a copy that dragged a newline along.
 */
export function parseJoinCode(input: string): JoinCodeParse {
  const stripped = input.replace(SEPARATORS, '').toUpperCase();

  if (stripped === '') {
    return { ok: false, reason: 'empty', detail: 'no code was entered' };
  }

  for (const character of stripped) {
    if (!JOIN_CODE_ALPHABET.includes(character)) {
      return { ok: false, reason: 'character', detail: `'${character}' is not in a join code` };
    }
  }

  if (stripped.length !== JOIN_CODE_LENGTH) {
    const wanted = String(JOIN_CODE_LENGTH);
    const got = String(stripped.length);
    return {
      ok: false,
      reason: 'length',
      detail: `a join code is ${wanted} characters, not ${got}`,
    };
  }

  return { ok: true, canonical: stripped };
}

function cryptoBytes(count: number): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(count));
}
